import { push } from "connected-react-router"
import { Button, ButtonGroup, IconCheck } from "dash-styled"
import { ReactComponent as IconClose } from "icons/lib/close-icon.svg"
import React, { Fragment, useCallback, useMemo } from "react"
import { useDispatch } from "react-redux"
import { useAppInstance } from "../../../../../../../Hooks/useAppInstance"
import { useAppTemplate } from "../../../../../../../Hooks/useAppTemplate"
import { useAsyncCommand } from "../../../../../../../Hooks/useAsyncCommand"
import {
    APIAppInstanceModel,
    isAppInstanceEnabled
} from "../../../../../../../Models/API/AppInstanceModel"
import { Protocol } from "../../../../../../../Models/API/AppTemplateModel"
import {
    ProjectRole,
    hasAppPermission
} from "../../../../../../../Models/Client/ProjectRole"
import {
    APIFeedbackMessage,
    APIFeedbackTag
} from "../../../../../../../Models/API/JobFeedbackModel"
import {
    APIJobQueryModel,
    APIJobModel,
    JobStatus,
    isEnqueueableJob
} from "../../../../../../../Models/API/JobModel"
import { hasValue } from "../../../../../../../Models/Client/FetchState"
import { FilterAndSortExpression } from "../../../../../../../Models/Client/FilterAndSortExpression"
import { MainThunkDispatch } from "../../../../../../../Reducers"
import { routePaths } from "../../../../../../../Routes"
import { NavButton } from "../../../../../../Common/NavButton/NavButton"
import { FeedbackMessages } from "./FeedbackMessages"
import {
    APITaskDetailsModel,
    UITaskModel
} from "../../../../../../../Models/API/TaskModel"

interface DetailViewSidebarProps {
    orgUsername: string
    roles: ProjectRole[]
    appID: string
    jobID: string
    filterAndSort: FilterAndSortExpression<APIJobQueryModel>
    job: APIJobModel
    onCorrectnessChange: (correct: boolean) => Promise<void>
    onEnqueue?: () => void
    instance: APIAppInstanceModel
    selectedJobs?: number[]
    feedbackMessages?: APIFeedbackMessage[]
    onSendFeedbackMessage: (message: string) => Promise<any>
    feedbackTags?: APIFeedbackTag[]
    isMessagesPath?: boolean
    canVerify: boolean
    taskList: UITaskModel[]
    selectedTask?: APITaskDetailsModel
}

export function DetailViewSidebar({
    orgUsername,
    roles,
    appID,
    jobID,
    filterAndSort,
    job,
    onCorrectnessChange,
    instance,
    selectedJobs,
    feedbackMessages,
    onSendFeedbackMessage,
    feedbackTags,
    onEnqueue,
    canVerify,
    isMessagesPath = false,
    taskList,
    selectedTask
}: DetailViewSidebarProps) {
    const isCorrect =
        selectedTask !== undefined
            ? selectedTask.correct === true
            : job.correct === true
    const isIncorrect =
        selectedTask !== undefined
            ? selectedTask.correct === false
            : job.correct === false
    const markAsCorrect = useAsyncCommand(
        isCorrect ? undefined : () => onCorrectnessChange(true)
    )
    const markAsIncorrect = useAsyncCommand(
        isIncorrect ? undefined : () => onCorrectnessChange(false)
    )

    const instanceFetch = useAppInstance(appID)
    const templateFetch = useAppTemplate(appID)
    const dispatch = useDispatch<MainThunkDispatch>()

    const onLabel = useCallback(
        () =>
            dispatch(
                push(
                    routePaths.jobDetailLabeling(
                        orgUsername,
                        appID,
                        job.id,
                        filterAndSort,
                        selectedJobs
                    ),
                    { isMessagesPath }
                )
            ),
        [
            dispatch,
            orgUsername,
            appID,
            job.id,
            filterAndSort,
            selectedJobs,
            isMessagesPath
        ]
    )

    const isAppEnabled = isAppInstanceEnabled(instance)

    const canLabel = useMemo(
        () =>
            hasValue(instanceFetch) &&
            !instanceFetch.value.canoticCrowd &&
            hasValue(templateFetch) &&
            templateFetch.value.protocol === Protocol.WEBSOCKET &&
            job.status === JobStatus.IN_PROGRESS,
        [instanceFetch, job.status, templateFetch]
    )

    const canEnqueue = useMemo(() => isEnqueueableJob(job), [job])

    const mainActions = useMemo(() => {
        if (!isAppEnabled) {
            return []
        }
        const actions = []

        canEnqueue &&
            actions.push(
                <Button
                    key="label-myself-action"
                    color="darkviolet"
                    variant="contained"
                    onClick={onEnqueue}
                    testId="job-detail-enqueue-button"
                >
                    Queue for processing
                </Button>
            )
        canLabel &&
            actions.push(
                <Button
                    key="label-myself-action"
                    color="darkviolet"
                    variant="contained"
                    onClick={onLabel}
                    testId="job-detail-label-button"
                >
                    {roles.length === 1 && roles[0] === "LABELER"
                        ? "Start processing"
                        : "Process \nmyself"}
                </Button>
            )
        job.status === JobStatus.COMPLETED &&
            hasAppPermission(roles, "job:review") &&
            canVerify &&
            actions.push(
                <Fragment key="review">
                    <ButtonGroup
                        key={
                            // include jobID in key to prevent unwanted animation on
                            // prev/next navigation (force DOM recreation instead of changing attributes)
                            `review-${jobID}`
                        }
                        equalWidth={true}
                    >
                        <Button
                            color={
                                isCorrect
                                    ? "positive"
                                    : isIncorrect
                                    ? "white"
                                    : "darkviolet"
                            }
                            className="flex opacity-100 shadow-none whitespace-pre border border-solid border-rhino-400 border-r-0 justify-center"
                            onClick={markAsCorrect.perform}
                            disabled={
                                markAsCorrect.perform === undefined ||
                                markAsCorrect.isProcessing
                            }
                            testId="mark-job-as-correct-button"
                        >
                            <IconCheck size="xl" className="-my-1" />
                            {markAsCorrect.isProcessing
                                ? "Saving..."
                                : "Correct"}
                        </Button>
                        <Button
                            color={
                                isIncorrect
                                    ? "negative"
                                    : isCorrect
                                    ? "white"
                                    : "darkviolet"
                            }
                            className="flex opacity-100 shadow-none whitespace-pre border border-solid border-rhino-400"
                            onClick={markAsIncorrect.perform}
                            disabled={
                                markAsIncorrect.perform === undefined ||
                                markAsIncorrect.isProcessing
                            }
                            testId="mark-job-as-incorrect-button"
                        >
                            <IconClose className="-my-1 p-1 w-6 h-6" />
                            {markAsIncorrect.isProcessing
                                ? "Saving..."
                                : "Incorrect"}
                        </Button>
                    </ButtonGroup>
                </Fragment>
            )

        job.status === JobStatus.COMPLETED &&
            hasAppPermission(roles, "task-design:update") &&
            actions.push(
                <NavButton
                    key="instructions"
                    color="white"
                    variant="contained"
                    to={routePaths.appCustomizeJobParameters(
                        orgUsername,
                        appID
                    )}
                >
                    Edit instructions
                </NavButton>
            )

        return actions
    }, [
        appID,
        canEnqueue,
        canLabel,
        canVerify,
        isAppEnabled,
        isCorrect,
        isIncorrect,
        job.status,
        jobID,
        markAsCorrect.isProcessing,
        markAsCorrect.perform,
        markAsIncorrect.isProcessing,
        markAsIncorrect.perform,
        roles,
        onEnqueue,
        onLabel,
        orgUsername
    ])

    return (
        <div
            className="flex flex-col justify-start px-4 flex-grow-0 flex-shrink-0 bg-smoke-200 pb-16 rounded-tr-xl overflow-scroll border-l"
            style={{ width: "300px" }}
            data-testid="detail-view-sidebar"
        >
            <div className="sticky top-0">
                <div className="pt-10 bg-smoke-200 bg-opacity-50 flex flex-col gap-2">
                    {mainActions}
                </div>
                <div className="h-5 bg-gradient-to-b from-smoke-210 to-transparent" />
            </div>
            <FeedbackMessages
                appID={appID}
                job={job}
                messages={feedbackMessages}
                onMessageSend={onSendFeedbackMessage}
                feedbackTags={feedbackTags}
                taskList={taskList}
            />
        </div>
    )
}
